import React, { useEffect } from 'react';
import {
  KatDropdown, KatIcon, KatOption, KatToastGroup,
} from '@amzn/katal-react';
import { useTranslation, getEnvironment, useMetrics } from 'src/utils';
import { useRouter } from 'next/router';
import { AppState } from 'src/redux/store';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  DEFAULT_MAKETPLACE_URL_HOST,
  MARKETPLACE_URL_TO_ID_MAP,
  MARKETPLACES,
  MARKETPLACES_EU,
  MARKETPLACES_FE,
  MARKETPLACES_NA,
} from 'src/constants/marketplaces';
import { HOME_PAGE } from 'src/constants/pages';
import { ENVIRONMENT } from 'src/constants/environments';
import { setShowMarketplaceBanner } from 'src/redux/slices/appNotifications';
import {
  selectMarketplaceId,
  updateUserPreferences,
  userInfoActions,
  selectUserPreferences,
  updateMarketplaceAction,
} from 'src/redux/slices/userInfo';
import { ButtonTypes } from 'src/constants/metrics';
import FetchStatus from 'src/types/FetchStatus';
import weblab from 'src/weblab/weblabService';
import WeblabId from 'src/weblab/weblabId';
import WeblabTreatment from 'src/weblab/weblabTreatment';
import { ProfileStatus } from 'src/types/UserInfo';

const MarketplaceSwitcher = () => {
  const { t } = useTranslation();
  const { clickMetric } = useMetrics();
  const { basePath, query } = useRouter();
  const dispatch = useAppDispatch();
  const environment = getEnvironment(ENVIRONMENT);
  const host = typeof window !== 'undefined' && window.location?.host;
  const {
    marketplaceIds: marketplacesSellerBelongsTo,
    selectedMarketplaceIdStatus,
    shouldSyncUserPreference,
    isInternal,
    profileStatus,
  } = useAppSelector(({ userInfo }: AppState) => userInfo);

  const marketplaceId = useAppSelector(
    (state: AppState) => selectMarketplaceId(state),
  );
  const userPreferences = useAppSelector(selectUserPreferences);
  const isPersistedPreference = profileStatus === ProfileStatus.registered
  && weblab.checkIfTreatmentEquals(
    WeblabId.SELLER_FORUMS_USER_PREF_STORAGE,
    WeblabTreatment.T1,
  );

  const hasMarketplaceUpdateFailed = isPersistedPreference
    && selectedMarketplaceIdStatus === FetchStatus.failed;

  const marketplaceOptions = [
    ...MARKETPLACES_NA,
    ...MARKETPLACES_EU,
    ...MARKETPLACES_FE,
  ];

  useEffect(() => {
    let id = marketplaceId;
    // marketplace preference has not been set in localStorage
    // fall back to url
    if (!id) {
      id = MARKETPLACE_URL_TO_ID_MAP[host || DEFAULT_MAKETPLACE_URL_HOST];
      dispatch(userInfoActions.changeMarketplace(id));
    }
    const { prevMarketplace } = query;
    if (typeof prevMarketplace === 'string' && id !== prevMarketplace) {
      dispatch(userInfoActions.setPrevMarketplace(prevMarketplace));
      dispatch(setShowMarketplaceBanner(true));
    }
  }, []);

  useEffect(() => {
    if (isPersistedPreference && shouldSyncUserPreference) {
      dispatch(updateUserPreferences({
        lastSelectedMarketplaceId: marketplaceId,
        sortByPref: userPreferences.threadListingSortBy,
        dateRangePref: userPreferences.threadListingDateRange,
      }));
    }
  }, [shouldSyncUserPreference]);

  // Only show dropdown options for marketplaces with valid urls for that environment
  const options = marketplaceOptions
    .sort()
    .map((name) => {
      const marketplace = MARKETPLACES[name];
      const id = marketplace[environment]!;
      return {
        id,
        icon: marketplace.flag,
        hasWriteAccess: isInternal || marketplacesSellerBelongsTo.includes(id),
      };
    });

  const writeAllowedMarketplaces = options.filter(
    (option) => !!option.hasWriteAccess,
  );
  const readOnlyMarketplaces = options.filter(
    (option) => !option.hasWriteAccess,
  );

  const renderOption = (option: { id: string, icon: KatIcon.Name }) => (
    <KatOption key={option.id} value={option.id}>
      <div className="flex items-center">
        <KatIcon name={option.icon} size="small" />
        <span className="ltr:ml-1 rtl:mr-1">{t(`marketplace_id_${option.id}`)}</span>
      </div>
    </KatOption>
  );

  function renderMyMarketPlaces() {
    if (writeAllowedMarketplaces.length > 0) {
      return (
        <>
          <KatOption data-testid="my-marketplaces-header" value="my-marketplaces" disabled>
            <p className="mb-0">{t('marketplace_my_marketplace')}</p>
            <span className="text-tiny">{t('marketplace_available_to_post')}</span>
          </KatOption>
          {writeAllowedMarketplaces.map(renderOption)}
        </>
      );
    }
    return null;
  }

  function renderOtherMarketplaces() {
    if (readOnlyMarketplaces.length > 0) {
      const header = writeAllowedMarketplaces.length === 0
        ? t('marketplaces')
        : t('marketplace_other_marketplaces');
      return (
        <>
          <KatOption data-testid="other-marketplaces-header" value="other-marketplaces" disabled>
            <p className="mb-0">{header}</p>
            <span className="text-tiny">{t('marketplace_read_only')}</span>
          </KatOption>
          {readOnlyMarketplaces.map(renderOption)}
        </>
      );
    }
    return null;
  }

  const handleOnChange = (e: KatDropdown.ChangeEvent) => {
    const id = e.detail.value;
    const replaceURL = `${basePath}${HOME_PAGE}?prevMarketplace=${marketplaceId}`;
    clickMetric({ metricValue: id, metricName: ButtonTypes.forumsMP });
    updateMarketplaceAction({
      marketplaceId: id,
      dispatch,
      isPersistedPreference,
      replaceURL,
    });
  };

  return (
    <>
      {
        hasMarketplaceUpdateFailed && (
        <KatToastGroup
          data-testid="marketplace-switcher-toast"
          toasts={[{
            id: 'market-place-change-failure',
            description: t('request_marketplaceUpdateFailureDescription'),
            variant: 'danger',
            persistent: true,
            actionText: t('refreshPage'),
            ctaClicked: () => window.location.reload(),
          }]}
        />
        )
      }
      <KatDropdown
        data-testid="marketplace-switcher"
        className="flex w-4/5 p-0 ltr:mr-0 rtl:ml-0"
        size="small"
        placeholder={t('marketplaces')}
        rich-selection-label
        value={marketplaceId!}
        onChange={handleOnChange}
        state={hasMarketplaceUpdateFailed ? 'error' : undefined}
      >
        { renderMyMarketPlaces() }
        { renderOtherMarketplaces() }
      </KatDropdown>
    </>
  );
};

export default MarketplaceSwitcher;
